import {AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject, Subject, takeUntil} from 'rxjs';
import {fuseAnimations} from '@fuse/animations';
import {saveAs} from 'file-saver';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {FuseAlertType} from '@fuse/components/alert';
import moment from 'moment';
import {AccountRole} from '@enums/AccountRole';
import {ReportsService} from '@services/reports.service';
import {ContractService} from '@services/contract.service';
import {RangeDates} from '@models/range-dates.interface';
import Timeout = NodeJS.Timeout;

export interface Alert {
    message: string;
    type: FuseAlertType;
}

enum ReportType {
    annual = 0,
    monthly = 1,
}

enum AlertType {
    success = 'success',
    warning = 'warning',
    error = 'error'
}

@Component({
    selector: 'app-providers-reports',
    templateUrl: './providers-reports.component.html',
    styleUrls: ['./providers-reports.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProvidersReportsComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input() providerId: string | null;

    dateRangeNotFound: boolean = false;
    period: string;
    years: Array<string> = [];
    months: Array<{ name: string; value: string }> = [];
    selectedYear$: BehaviorSubject<string> = new BehaviorSubject(null);
    selectedMonth$: BehaviorSubject<string> = new BehaviorSubject(null);
    reportAlert$: BehaviorSubject<Alert | null> = new BehaviorSubject<Alert | null>(null);
    reportAlertTime$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
    disableDownloadAnnualButton: boolean = false;
    disableDownloadMonthlyButton: boolean = false;
    progressBarColorClass: string = '';
    userRole: AccountRole;
    ACCOUNT_ROLE: typeof AccountRole = AccountRole;
    private _minContractsDate: moment.Moment = null;
    private _maxContractsDate: moment.Moment = null;
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private readonly ngZone: NgZone,
        private readonly _service: ReportsService,
        private readonly _contractsService: ContractService
    ) {
        moment.locale('es');
    }

    async downloadAnnualReport(): Promise<void> {
        if ((this.userRole === AccountRole.master || this.userRole === AccountRole.semiMaster) && !this.providerId) {
            this.showReportAlert({
                type: AlertType.error,
                message: 'No se ha seleccionado un proveedor para descargar el reporte anual.'
            }, ReportType.annual);
            return;
        }
        if (!this.selectedYear$.value) {
            this.showReportAlert({
                type: AlertType.error,
                message: 'Debes seleccionar un año para descargar el reporte anual.'
            }, ReportType.annual);
            return;
        }

        const year: string = this.selectedYear$.value;

        this._service.getContractorAnnuallyReportFile(this.providerId, this.selectedYear$.value).subscribe({
            next: (response: any): void => {
                saveAs(response, `reporte-anual-${this.selectedYear$.value}-sise.xlsx`);
                this.showReportAlert({
                    type: AlertType.success,
                    message: `¡Se ha descargado el reporte del año ${year} correctamente!`
                }, ReportType.annual);
            },
            error: (error: HttpErrorResponse): void => {
                if (error.status === HttpStatusCode.NoContent) {
                    this.showReportAlert({
                        type: AlertType.warning,
                        message: `No se encontró el reporte anual para el año ${year}.`
                    }, ReportType.annual);
                } else {
                    this.showReportAlert({
                        type: AlertType.error,
                        message: `Ocurrió un error al intentar descargar el reporte del año ${year}.`
                    }, ReportType.annual);
                }
            }
        });
    }

    async downloadMonthlyReport(): Promise<void> {
        if ((this.userRole === AccountRole.master || this.userRole === AccountRole.semiMaster) && !this.providerId) {
            this.showReportAlert({
                type: AlertType.error,
                message: 'No se ha seleccionado un proveedor para descargar el reporte mensual.'
            }, ReportType.monthly);
            return;
        }
        if (!this.period) {
            this.showReportAlert({
                type: AlertType.error,
                message: 'Debes seleccionar un mes y un año para descargar el reporte mensual.'
            }, ReportType.monthly);
            return;
        }

        const month: string = this.getMonthName(this.selectedMonth$.value);
        const year: string = this.selectedYear$.value;

        this._service.getContractorMonthlyReportFile(this.providerId, this.period).subscribe({
                next: (response: Blob): void => {
                    saveAs(response, `reporte-mensual-${this.period}-sise.xlsx`);
                    this.showReportAlert({
                        type: AlertType.success,
                        message: `¡Se ha descargado el reporte del mes ${month} y año ${year} correctamente!`
                    }, ReportType.monthly);
                },
                error: (error: HttpErrorResponse): void => {
                    if (error.status === HttpStatusCode.NoContent) {
                        this.showReportAlert({
                            type: AlertType.warning,
                            message: `No se encontró el reporte para el año ${year} y mes ${month}.`
                        }, ReportType.monthly);
                    } else {
                        this.showReportAlert({
                            type: AlertType.error,
                            message: `Ocurrió un error al intentar descargar el reporte del mes ${month} y año ${year}.`
                        }, ReportType.monthly);
                    }
                }
            }
        );
    }

    ngAfterViewInit(): void {
        this.userRole = JSON.parse(localStorage.getItem('user') ?? '')?.rol;
    }

    ngOnInit(): void {
        this._contractsService.getContractsDateRangeByContractor(this.providerId).subscribe({
            next: (response: RangeDates): void => {
                this._minContractsDate = response?.minDate ? moment(response.minDate) : null;
                this._maxContractsDate = response?.maxDate ? moment(response.maxDate) : null;
                const years: string[] = this.getYears();
                this.years = years;
                if (years.length > 0) {
                    this.selectedYear$.next(years[0]);
                }
            },
            error: (): void => {
                this.years = [];
                this.months = [];
                this.selectedYear$.next(null);
                this.selectedMonth$.next(null);
                this.dateRangeNotFound = true;
            }
        })

        this.selectedYear$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((year: string): void => {
                const months: { name: string; value: string }[] = this.getMonths(year);
                this.months = months;
                this.selectedMonth$.next(months[0]?.value);
            });

        this.selectedMonth$.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((month: string): void => {
                if (month && this.selectedYear$.value) {
                    this.period = `${month}-${this.selectedYear$.value}`;
                } else {
                    this.period = null;
                }
            });

        const years: string[] = this.getYears();
        if (years.length > 0) {
            this.selectedYear$.next(years[0]);
        }
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    getMonthName(monthNumber: string): string {
        if (!monthNumber) {
            return '';
        }
        return moment().month(Number(monthNumber) - 1).format('MMMM');
    }

    validateNotNullYear(year: string | null): string {
        if (!year) {
            return '';
        }
        return year;
    }

    private getYears(): Array<string> {
        const registerYear: number = this._minContractsDate ? this._minContractsDate.year() : null;
        const currentYear: number = this._maxContractsDate ? this._maxContractsDate.year() : null;
        if (!registerYear || !currentYear) {
            return [];
        }
        const diff: number = currentYear - registerYear + 1;
        return [...new Array(diff).keys()].map((i: number): string => (i + registerYear).toString()).reverse();
    }

    private getMonths(year: string): Array<{ name: string; value: string }> {
        if (!this._minContractsDate?.year() || !this._maxContractsDate?.year()) {
            return [];
        }

        const registerYear: number = this._minContractsDate ? Number(this._minContractsDate.year()) : null;
        const currentYear: number = this._maxContractsDate ? Number(this._maxContractsDate.year()) : null;
        const numberYear: number = Number(year);
        const registerMonth: number = Number(this._minContractsDate.month());
        const currentMonth: number = Number(this._maxContractsDate.month());

        switch (true) {
            case registerYear === numberYear:
                return moment.months().slice(registerMonth).map((name: string, index: number): {
                    name: string;
                    value: string
                } => ({
                    name,
                    value: (index + registerMonth + 1).toString().padStart(2, '0')
                }));
            case registerYear < numberYear && numberYear < currentYear:
                return moment.months().map((name: string, index: number): { name: string; value: string } => ({
                    name,
                    value: (index + 1).toString().padStart(2, '0')
                }));
            case currentYear === numberYear:
                return moment.months().slice(0, currentMonth + 1).map((name: string, index: number): {
                    name: string;
                    value: string
                } => ({
                    name,
                    value: (index + 1).toString().padStart(2, '0')
                }));
            default:
                return [];
        }
    }

    private showReportAlert(alert: Alert, reportType: ReportType): void {
        const alertDelay: number = 5000; // total time to show alert in milliseconds
        const refreshTime: number = 150; // update value of progress bar every x milliseconds
        const progressBarTotal: number = 100; // total value of progress bar needs to decrease to cero
        const progressBarInterval: number = progressBarTotal / (alertDelay / refreshTime);

        switch (alert.type) {
            case AlertType.success:
                this.progressBarColorClass = 'progress-bar-success';
                break;
            case AlertType.warning:
                this.progressBarColorClass = 'progress-bar-warning';
                break;
            case AlertType.error:
                this.progressBarColorClass = 'progress-bar-error';
                break;
        }

        if (reportType === ReportType.annual) {
            this.disableDownloadAnnualButton = true;
        }
        if (reportType === ReportType.monthly) {
            this.disableDownloadMonthlyButton = true;
        }

        this.ngZone.run((): void => {
            this.reportAlert$.next(alert);
            this.reportAlertTime$.next(progressBarTotal);

            const interval: Timeout = setInterval((): void => {
                const currentValue: number = this.reportAlertTime$.value;
                if (currentValue > 0) {
                    this.reportAlertTime$.next(currentValue - progressBarInterval);
                } else {
                    clearInterval(interval);
                }
            }, refreshTime);

            setTimeout((): void => {
                this.reportAlert$.next(null);
                this.disableDownloadAnnualButton = false;
                this.disableDownloadMonthlyButton = false;
            }, alertDelay);
        });
    }
}
